import { Icon } from '@/atoms/icon'
import cn from 'classnames'
import React, { forwardRef, useState } from 'react'
import ReactSelect, {
  CSSObjectWithLabel,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  MultiValueRemoveProps,
  OptionProps,
  components,
} from 'react-select'
import { WidgetInputReactSelectProps } from '../../widget-inputs'
import { PuiColor } from '@/types'
import styles from '../../widget-inputs.module.scss'

let colorIconDefault: PuiColor = 'gray-7'

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name="keyboard_arrow_down" size="lg" color={colorIconDefault} />
    </components.DropdownIndicator>
  )
}

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <Icon name="delete_outline" size="lg" color={colorIconDefault} />
    </components.ClearIndicator>
  )
}

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <Icon name="close" size="lg" color={colorIconDefault} />
    </components.MultiValueRemove>
  )
}

const controlStyles = {
  base: styles['react-select__control'],
  focus: styles['react-select__control--focus'],
  nonFocus: styles['react-select__control--no-focus'],
}
const placeholderStyles = styles['react-select__placeholder']
const placeholderRequiredStyles = `${styles['react-select__placeholder']} ${styles['react-select__placeholder--required']}`
const selectInputStyles = styles['react-select__input']
const valueContainerStyles = styles['react-select__value']

const { ValueContainer, Placeholder } = components
const CustomValueContainer = ({ children, ...props }: any) => {
  const placeholderText =
    props.selectProps.placeholder || props.selectProps.label
  const helpText =
    props.selectProps.isFocused || props.hasValue
      ? props.selectProps.label
      : placeholderText
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.selectProps.isFocused}>
        {helpText}
      </Placeholder>
      {React.Children.map(children, (child: any) =>
        child && child.key !== 'placeholder' ? child : null
      )}
    </ValueContainer>
  )
}

export const WidgetInputReactSelect = forwardRef<
  any,
  WidgetInputReactSelectProps
>((props, ref) => {
  const { iconColor, inputStyles, labelStyles } = props
  const [focused, setFocused] = useState(false)
  iconColor ? (colorIconDefault = iconColor as PuiColor) : undefined
  return (
    <div
      className={
        props?.error ? styles['form__react__select__error'] : undefined
      }
      data-testid="widget-input-wrapper"
    >
      <ReactSelect
        ref={ref}
        placeholder={props?.label}
        isMulti={false}
        closeMenuOnSelect={true}
        hideSelectedOptions={true}
        isClearable={true}
        isDisabled={props?.disabled}
        styles={{
          multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            whiteSpace: 'normal',
            overflow: 'visible',
          }),
          control: (baseStyles: CSSObjectWithLabel, state: any) => ({
            ...baseStyles,
            transition: 'none',
            outline: 'none',
            border:
              inputStyles && inputStyles['border']
                ? inputStyles['border']
                : '1px solid var(--pui-color-gray-2)',
            boxShadow: 'none',
            pointerEvents: 'auto',
            cursor: state.isDisabled ? 'not-allowed' : 'default',
            opacity: state.isDisabled ? '0.5' : '1',
            background:
              inputStyles && inputStyles['background']
                ? inputStyles['background']
                : 'var(--pui-color-white)',
            '&:hover': {
              border: state.selectProps.isFocused
                ? '1px solid var(--pui-color-gray-2)'
                : '1px solid var(--pui-color-gray-2)',
            },
          }),
          valueContainer: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            overflow: 'visible',
            paddingLeft: '0',
          }),
          placeholder: (baseStyles: CSSObjectWithLabel, state: any) => ({
            ...baseStyles,
            position: 'absolute',
            color:
              labelStyles && labelStyles['color']
                ? labelStyles['color']
                : 'var(--pui-color-gray-2)',
            background: 'transparent',
            top:
              state.hasValue ||
              state.selectProps.inputValue ||
              state.selectProps.isFocused
                ? '-120%'
                : '4px',
            transition: 'top 0.2s',
            pointerEvents: 'auto',
            cursor: state.isDisabled ? 'not-allowed' : 'text',
          }),
          option: (baseStyles: CSSObjectWithLabel, state: OptionProps) => ({
            ...baseStyles,
            ...labelStyles,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
          }),
          input: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
          }),
          singleValue: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color:
              labelStyles && labelStyles['color']
                ? labelStyles['color']
                : 'black',
          }),
          dropdownIndicator: (
            baseStyles: CSSObjectWithLabel,
            state: DropdownIndicatorProps
          ) => ({
            ...baseStyles,
            color: iconColor as PuiColor,
            transition: 'all .6s ease',
            transform: state.selectProps.menuIsOpen
              ? 'rotate(180deg)'
              : 'rotate(0)',
          }),
          menu: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            background:
              inputStyles && inputStyles['background']
                ? inputStyles['background']
                : 'var(--pui-color-white)',
            border:
              inputStyles && inputStyles['border']
                ? inputStyles['border']
                : '1px solid var(--pui-color-gray-2)',
          }),
        }}
        components={{
          DropdownIndicator,
          ClearIndicator,
          MultiValueRemove,
          IndicatorSeparator: () => null,
          ValueContainer: CustomValueContainer,
        }}
        className={styles['react-select']}
        classNames={{
          control: (state: any) =>
            cn(
              state.isFocused ? controlStyles.focus : controlStyles.nonFocus,
              controlStyles.base
            ),
          placeholder: () =>
            props.required ? placeholderRequiredStyles : placeholderStyles,
          input: () => selectInputStyles,
          valueContainer: () => valueContainerStyles,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        isFocused={focused}
        noOptionsMessage={() => props?.noChoices}
        {...props}
      />
      {props?.error && (
        <span
          className={`${styles['form__message']} ${styles['form__message--visible']} ${styles['form__message--error']}`}
        >
          {props?.error}
        </span>
      )}
    </div>
  )
})

WidgetInputReactSelect.displayName = 'WidgetInputReactSelect'
